<template>
  <section class="files-section grid-block">
    <header>
      <span>Файлы</span>

      <!-- Одна сущность: кнопки загрузки и «Скачать всё» в заголовке. -->
      <div
        v-if="isSingleMode"
        class="header-actions"
      >
        <VFilesUploadButton
          v-if="canUpload"
          :entity="entities[0]"
          theme="link"
        >
          Загрузить файл
        </VFilesUploadButton>

        <VButton
          :disabled="isEmptyRow(data[0])"
          theme="link"
          @click="downloadGroup(data)"
        >
          <template #icon>
            <VIcon
              material="download"
              theme="outlined"
            />
          </template>
          Скачать всё
        </VButton>
      </div>

      <!-- Несколько сущностей: одна кнопка загрузки через диалог выбора. -->
      <div v-else>
        <VButton
          v-if="canUpload"
          theme="link"
          @click="openSelectUploadDialog"
        >
          <template #icon>
            <VIcon
              theme="outlined"
              material="upload"
            />
          </template>
          Загрузить файлы
        </VButton>
      </div>
    </header>

    <div class="content">
      <VDataGridV2
        :data="data"
        class="files-grid"
        :class="{ 'hide-group': isSingleMode && inherited.length === 0 }"
        clickable
        group-children-property="children"
        layout="fill"
        :context-menu="FilesService.getContextMenu(inherited)"
        :group-context-menu="FilesService.groupContextMenu"
        @row:click="row => preview(row)"
      >
        <template #empty>
          <div v-if="isFilesGridLoading">
            Файлы загружаются, подождите, пожалуйста 🙏...
          </div>
          <div v-else>
            Нет файлов
          </div>
        </template>

        <!-- Группировка для множественных сущностей -->
        <VDataGridGroupV2>
          <template #default="{ parent, children }">
            {{ parent.title }}

            <!-- Справочная информация в шапке группы. Пусто по умолчанию. -->
            <slot
              v-if="parent.isInherited === false"
              name="group-hint"
              :entity="parent.data"
            />

            <div v-if="$core.utils.isEmptyArray(children) && isFilesGridLoading">
              Файлы загружаются, подождите, пожалуйста 🙏...
            </div>

            <div
              v-if="parent.isInherited === true && $core.utils.isEmptyArray(children) && isFilesGridLoading === false"
              class="description"
            >
              Унаследованных файлов нет
            </div>
          </template>
        </VDataGridGroupV2>

        <!-- Колонка иконки расширения -->
        <VDataGridColumnV2
          id="extension"
          content-align="center"
          width="75px"
        >
          <template #default="{ row }">
            <div
              :class="{ 'grey': row.isAlreadyDownloaded }"
            >
              <img
                :src="row.fileIcon"
                :alt="row.extension + ' file'"
              />
              <div class="description">
                {{ row.formattedSize }}
              </div>
            </div>
          </template>
        </VDataGridColumnV2>

        <!-- Колонка названия файла -->
        <VDataGridColumnV2
          id="name"
          width="365px"
          content-class="ellipsis"
        >
          <template #label>
            <div>
              Наименование файла
            </div>
            <div class="description">Дата загрузки</div>
          </template>

          <template #default="{ row }">
            <div :title="row.name" class="ellipsis">
              {{ row.name }}
              <div class="description">
                {{ row.createdAt.format('DD|MMMM|HH|mm') }}
              </div>
            </div>
          </template>
        </VDataGridColumnV2>

        <!-- Колонка источника: заполнена только у унаследованных файлов -->
        <VDataGridColumnV2
          v-if="inherited.length > 0"
          id="source"
          label="Источник"
          width="120px"
          content-class="ellipsis"
        >
          <template #default="{ row }">
            <div
              v-if="getInheritedSource(row) !== undefined"
              :title="getInheritedSource(row).name"
              class="ellipsis"
            >
              {{ getInheritedSource(row).name }}
              <div class="description">
                {{ row.parentEntity.id }}
              </div>
            </div>
          </template>
        </VDataGridColumnV2>

        <!-- Колонка действий -->
        <VDataGridColumnV2
          id="extra-actions"
          width="45px"
          content-align="right"
        >
          <template #default="{ row }">
            <div
              class="action-container"
              @click.stop
            >
              <VButton
                :title="row.isAlreadyDownloaded ? 'Файл уже был скачан' : 'Скачать файл'"
                theme="default"
                @click="FilesService.downloadFile(row)"
              >
                <template #icon>
                  <VIcon
                    :class="{ 'downloaded': row.isAlreadyDownloaded }"
                    material="file_download"
                  />
                </template>
              </VButton>
            </div>
          </template>
        </VDataGridColumnV2>
      </VDataGridV2>
    </div>

    <!-- Overlay для drag&drop -->
    <div
      v-if="
          isActive === true &&
          FilesService.isDragging &&
          !FilesService.isDragDropLocked &&
          !isSelectUploadDialogOpen
        "
      class="drop-overlay-container"
    >

      <!-- одна сущность: потребность, товар, партнер -->

      <template v-if="isSingleMode">
        <VFilesDropZone
          v-for="section in overlayData"
          :key="section.key"
          :max-one-file="maxOneFile"
          :entity="entitiesMap.get(section.key)"
          :class="{ 'fullscreen': isSingleMode }"
        />
      </template>

      <!-- несколько сущностей: заказ/позиция -->

      <template v-else>
        <VFilesDropZone
          v-for="section in overlayData"
          :key="section.key"
          :max-one-file="maxOneFile"
          :entity="entitiesMap.get(section.key)"
          :style="{
              position: 'absolute',
              left:   section.left + 'px',
              top:    section.top + 'px',
              width:  section.width + 'px',
              height: section.height + 'px'
            }"
        />
      </template>
    </div>
  </section>
</template>

<style lang="scss" src="./FilesList.scss"/>

<script lang="ts">

import FilesService  from '@/core/services/Files/FilesService';
import FileDescriptor from '@/core/services/Files/FileDescriptor';
import EntityPointer from "@/domains/EntityPointer/EntityPointer";

/**
 * Строка грида: сущность (EntityPointer) с прикреплёнными к ней файлами.
 * id — ключ вида "typeName:id", используется датагридом и для матчинга overlay.
 * title — заголовок группы.
 * isInherited — группа унаследованных файлов: только чтение, без загрузки, data === null.
 */
type FilesGridRow = {
  id:          string;
  data:        EntityPointer | null;
  title:       string;
  children:    FileDescriptor[];
  isInherited: boolean;
};

/** Прямоугольник дроп-зоны для overlay (single — на весь экран, multi — по группам грида). */
type OverlaySection = {
  key:    string;
  left:   number;
  top:    number;
  right?: number;
  bottom?: number;
  width:  number;
  height: number;
};

export default defineComponent({
  name: 'FilesList',

  props: {
    /** Сущности, к которым привязаны файлы. Одна → single mode, несколько → группировка по сущностям. */
    entities: {
      type:     Array as PropType<EntityPointer[]>,
      required: true,
    },
    /**
     * Унаследованные сущности: их файлы показываются одной группой только на чтение,
     * с колонкой источника. Загрузки и дроп-зон для них нет.
     */
    inherited: {
      type:    Array as PropType<EntityPointer[]>,
      default: () => [],
    },
    /** Режим одного файла — скрывает кнопки загрузки если файл уже есть. */
    maxOneFile: {
      type:    Boolean,
      default: false,
    },
  },

  emits: ['file:isDragging'],

  setup() {
    return {
      FilesService,
    };
  },

  data() {
    return {
      isFilesGridLoading:       false,
      isSelectUploadDialogOpen: false,
      data:                     [] as FilesGridRow[],
    };
  },

  created() {
    this.$core.eventBus.FilesUploaded.on(this.handleFilesEvent);
    this.$core.eventBus.FilesDeleted.on(this.handleFilesEvent);
    /** если файл не дотащили до дроп зоны, то вызываем диалог с выбором сущности */
    this.$core.eventBus.FilesDroppedOutsideZone.on(this.openSelectUploadDialog);
  },

  mounted() {
    FilesService.registerFilesList(this.$.uid);
    this.fetchData();
  },

  beforeUnmount() {
    FilesService.unregisterFilesList(this.$.uid);
    this.$core.eventBus.FilesUploaded.off(this.handleFilesEvent);
    this.$core.eventBus.FilesDeleted.off(this.handleFilesEvent);
    this.$core.eventBus.FilesDroppedOutsideZone.off(this.openSelectUploadDialog);
  },

  watch: {
    /** Список стал верхним (закрыли диалог над ним) — восстанавливаем свою блокировку drag&drop */
    isActive(value: boolean) {
      if (value === true) {
        this.applyLockState();
      } else {
        // блокировкой теперь управляет верхний список
      }
    },
  },

  computed: {
    /** Верхний из смонтированных FilesList: только он обслуживает drag&drop */
    isActive(): boolean {
      return FilesService.isActiveFilesList(this.$.uid);
    },

    canUpload(): boolean {
      if (!this.maxOneFile) {
        return true;
      }
      else {
        return this.isEmptyRow(this.data[0]);
      }
    },

    isSingleMode(): boolean {
      return this.entities.length === 1;
    },

    overlayData(): OverlaySection[] {
      let raw: OverlaySection[] = [];

      if (this.isSingleMode) {
        raw = [{
          key:    `${this.entities[0].typeName}:${this.entities[0].id}`,
          left:   0,
          top:    0,
          width:  window.innerWidth,
          height: window.innerHeight,
        }];
      }
      else if (FilesService.isDragging) {
        raw = this.getOverlayData();
      }

      return raw.filter(section => this.entitiesMap.has(section.key));
    },

    entitiesMap(): Map<string, EntityPointer> {
      return new Map(this.entities.map(entity => [entity.typeName + ':' + entity.id, entity]));
    },

    inheritedMap(): Map<string, EntityPointer> {
      return new Map(this.inherited.map(entity => [entity.typeName + ':' + entity.id, entity]));
    },
  },

  methods: {
    async downloadGroup(parent: FilesGridRow | FilesGridRow[]) {
      // строка
      const entity = Array.isArray(parent) ? parent[0] : parent;

      // у строки есть поле с детьми, и с информацией о сущности к которой они прикрепляются
      const { title, children } = entity;

      /** для одной сущности ?? для конкретной из нескольких */

      if (await this.$core.dialogs.confirm(
        `Вы уверены, что хотите скачать все файлы?`,
        `${title}`
      )) {
        children.forEach(file => {
          FilesService.downloadFile(file);
        });
      }
    },

    isEmptyRow(row?: FilesGridRow): boolean {
      return this.$core.utils.isEmptyArray(row?.children);
    },

    /** Унаследованная сущность, к которой прикреплён файл. undefined — файл свой. */
    getInheritedSource(file: FileDescriptor): EntityPointer | undefined {
      return this.inheritedMap.get(file.parentEntity.typeName + ':' + file.parentEntity.id);
    },

    openSelectUploadDialog({ files }: { files: File[] }) {
      if (FilesService.isDragDropLocked || this.isActive === false) {
        return;
      }

      // dataTransfer.files - это readonly свойство.
      // из-за этого мы не можем срезать файлы, мы можем только кинуть ошибку
      if (this.maxOneFile && files.length > 1) {
        this.$core.error('Вы можете прикрепить только один файл');
        return;
      }

      this.isSelectUploadDialogOpen = true;
      this.$core.dialogs.FilesSelectUploadDialog.show(
        {
          entities:     this.entities,
          droppedFiles: files, // файлы, которые перетащили вне дроп зоны
        },
        {
          'close': () => {
            this.isSelectUploadDialogOpen = false;
            FilesService.resetDragState();
          }
        }
      );
    },

    async fetchData() {
      this.isFilesGridLoading = true;

      // получаем список файлов от сервиса: свои и унаследованные одним запросом
      const data = await FilesService.getList([...this.entities, ...this.inherited]);
      // готовим данные с учетом особенностей группировки
      this.data = this.prepareData(data);
      this.isFilesGridLoading = false;

      // После обновления data пересчитываем блокировку drag&drop:
      // в режиме maxOneFile дроп-зона должна блокироваться, если файл уже прикреплён.
      this.applyLockState();
    },

    refresh() {
      return this.fetchData();
    },

    /**
     * Блокировка/разблокировка drag&drop в зависимости от canUpload.
     * Вызывается после каждого обновления data (fetchData) — заменяет watch
     * на canUpload, который был раньше.
     */
    applyLockState() {
      if (this.isActive === false) {
        // неактивный список блокировкой не управляет — это делает верхний
      }
      else if (this.canUpload === true) {
        FilesService.unlockDragDrop();
      }
      else {
        FilesService.lockDragDrop();
      }
    },

    async preview(file: FileDescriptor) {
      if (!file.isPreviewable) {
        return;
      }
      /** После превью нужно показать, что он уже скачен */
      await FilesService.previewFile(file);
      await this.fetchData();
    },

    /**
     * Преобразует ответ сервера в формат для VDataGridV2 с группировкой.
     *
     * Сервер отвечает по-разному в зависимости от количества сущностей:
     * - Одна сущность (потребность, товар, партнёр): плоский массив файлов
     * - Несколько сущностей (заказ + позиции): объект { "typeName:id": файлы[] }
     *
     * Метод нормализует оба формата в единую структуру для датагрида:
     * [{ id: "typeName:id", data: entity, children: файлы[] }, ...]
     *
     * Файлы унаследованных сущностей собираются в одну группу в конце,
     * в порядке перечисления сущностей в пропе inherited.
     */
    prepareData(data: FileDescriptor[] | Record<string, FileDescriptor[]> | null): FilesGridRow[] {
      if (!Array.isArray(this.entities) || this.entities.length === 0) {
        return [];
      }

      let normalized: Record<string, FileDescriptor[]>;

      // Если пришёл плоский массив (single mode) — оборачиваем в объект с ключом,
      // чтобы дальше обрабатывать единообразно
      if (Array.isArray(data)) {
        const singleEntity = this.entities[0];
        if (!singleEntity) {
          return [];
        }

        normalized = {
          [`${singleEntity.typeName}:${singleEntity.id}`]: data,
        };
      }
      else {
        normalized = data ?? {};
      }

      // Для каждой сущности из props достаём её файлы из нормализованного объекта
      const result: FilesGridRow[] = this.entities.map(entity => {
        const dataKey         = `${entity.typeName}:${entity.id}`;
        const entityFilesData = normalized[dataKey] || [];

        return {
          id:          dataKey,
          data:        entity,
          title:       entity.name ?? '',
          children:    entityFilesData,
          isInherited: false,
        };
      });

      if (this.inherited.length > 0) {
        result.push({
          id:          'inherited',
          data:        null,
          title:       'Унаследованные файлы',
          children:    this.inherited.flatMap(entity => normalized[`${entity.typeName}:${entity.id}`] || []),
          isInherited: true,
        });
      } else {
        // унаследованных сущностей нет — группу не рисуем
      }

      return result;
    },

    // определяем является ли событие релевантным именно для этого списка файлов и обновляем его если это так
    handleFilesEvent(eventData: { entity?: EntityPointer }) {
      if (eventData.entity) {
        const isRelevant = this.entities.some(entity =>
          entity.typeName === eventData.entity!.typeName &&
          entity.id === eventData.entity!.id
        );
        if (isRelevant) {
          this.refresh();
        }
      }
      else {
        this.refresh();
      }
    },

    /**
     * Вычисляет координаты прямоугольника для группы строк
     * @param headerRow - элемент строки-заголовка с классом group-header
     * @param origin - прямоугольник секции, от которого отсчитываются координаты
     */
    getGroupBounds(headerRow: HTMLElement, origin: DOMRect): OverlaySection {
      if (!headerRow || !headerRow.classList.contains('group-header')) {
        throw new Error('Передан некорректный элемент группы-заголовка');
      }
      // Получаем прямоугольник заголовка группы
      const headerRect = headerRow.getBoundingClientRect();
      // Находим последнюю строку текущей группы
      let lastRow: HTMLElement = headerRow;
      let nextRow = headerRow.nextElementSibling;
      while (nextRow && !nextRow.classList.contains('group-header')) {
        lastRow = nextRow as HTMLElement;
        nextRow = nextRow.nextElementSibling;
      }
      const lastRowRect = lastRow.getBoundingClientRect();
      // Вычисляем координаты общего прямоугольника относительно секции
      const sectionData: OverlaySection = {
        key:    headerRow.dataset.rowIdentity || '',
        left:   Math.min(headerRect.left, lastRowRect.left) - origin.left,
        top:    headerRect.top - origin.top,
        right:  Math.max(headerRect.right, lastRowRect.right) - origin.left,
        bottom: lastRowRect.bottom - origin.top,
        width:  0,
        height: 0,
      };
      sectionData.width  = sectionData.right! - sectionData.left;
      sectionData.height = sectionData.bottom! - sectionData.top;
      return sectionData;
    },

    /**
     * Функция для получения границ всех групп на странице
     */
    getOverlayData(): OverlaySection[] {
      // Оверлей позиционируется абсолютом внутри секции, а getBoundingClientRect
      // отдаёт координаты окна — отсчитываем от прямоугольника секции
      const origin = this.$el.getBoundingClientRect();

      const groupHeaders = this.$el.querySelectorAll<HTMLElement>('.group-header');

      // Если есть группы — собираем bounds по группам
      if (groupHeaders.length > 0) {
        return Array.from(groupHeaders)
          .map(header => this.getGroupBounds(header, origin));
      }

      // Если групп нет — возвращаем bounds всего грида
      const gridEl = this.$el.querySelector('.files-grid');
      if (!gridEl) {
        return [];
      }

      const rect = gridEl.getBoundingClientRect();
      return [{
        key:    this.entities[0].typeName + ':' + this.entities[0].id,
        left:   rect.left - origin.left,
        top:    rect.top - origin.top,
        right:  rect.right - origin.left,
        bottom: rect.bottom - origin.top,
        width:  rect.width,
        height: rect.height,
      }];
    },
  },
});
</script>
